/**
 * Administrator panel of public identifiers (TASK-0060).
 *
 * The panel answers the two questions an administrator actually has, and nothing else:
 *
 * - **"who is this handle?"** — a lookup by the handle quoted in a support conversation, which is what the field exists
 *   for. A free handle is an answer (`404`), not an error, so the screen says so rather than drawing a failure;
 * - **"what may nobody take?"** — the blacklist, as an exact set. The rows state the match rule on screen, because an
 *   administrator who expected `ceo*` behaviour would be surprised by a list that reserved only `ceo`.
 *
 * The rename sits beside the lookup, because that is the only place it can start from: an administrator finds the account
 * by its current handle and gives it a new one. It is the *only* path to a rename — the owner cannot move a handle at all.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Input, LtrInput } from '@/components/ui/input';
import { JalaliDate, LtrText } from '@/components/ui/direction';
import {
  Alert,
  Badge,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Spinner,
} from '@/components/ui/surface';
import {
  useBlacklistPublicIdentifier,
  useChangeUserPublicIdentifier,
  usePublicIdentifierBlacklist,
  useReleasePublicIdentifier,
  useUserByPublicIdentifier,
} from '@/features/admin/hooks/usePublicIdentifiers';
import type { AdminUserAccount } from '@/features/admin/api/adminPublicIdentifierApi';
import { useProblemMessage } from '@/lib/errors';

/** Renders the handle lookup, the rename and the blacklist. */
export function PublicIdentifierAdmin() {
  const { t } = useTranslation();

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('admin.publicIdentifiers.title')}</CardTitle>
        <CardDescription>{t('admin.publicIdentifiers.subtitle')}</CardDescription>
      </CardHeader>

      <CardContent className="flex flex-col gap-6">
        <AccountLookup />
        <div className="border-t border-border pt-4">
          <BlacklistSection />
        </div>
      </CardContent>
    </Card>
  );
}

/** The lookup-by-handle box and, once an account is found, the rename form. */
function AccountLookup() {
  const { t } = useTranslation();
  const messageFor = useProblemMessage();

  const [query, setQuery] = useState('');
  const [submitted, setSubmitted] = useState('');
  const [draft, setDraft] = useState('');

  const account = useUserByPublicIdentifier(submitted);
  const rename = useChangeUserPublicIdentifier();

  return (
    <section className="flex flex-col gap-3">
      <h3 className="text-sm font-medium">{t('admin.publicIdentifiers.lookupTitle')}</h3>
      <p className="text-xs text-muted-foreground">{t('admin.publicIdentifiers.lookupHint')}</p>

      <form
        className="flex flex-wrap items-end gap-2"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          rename.reset();
          setDraft('');
          setSubmitted(query.trim());
        }}
      >
        <div className="flex min-w-[16rem] flex-1 flex-col gap-1">
          <label className="text-xs text-muted-foreground" htmlFor="public-identifier-lookup">
            {t('auth.publicIdentifier')}
          </label>
          <LtrInput
            id="public-identifier-lookup"
            value={query}
            autoComplete="off"
            spellCheck={false}
            placeholder={t('auth.publicIdentifierPlaceholder')}
            onChange={(event) => setQuery(event.target.value)}
          />
        </div>

        <Button type="submit" disabled={query.trim().length === 0}>
          {t('admin.publicIdentifiers.lookupAction')}
        </Button>
      </form>

      {account.isFetching ? (
        <div className="flex justify-center p-2">
          <Spinner />
        </div>
      ) : null}

      {account.isError ? <Alert variant="destructive">{messageFor(account.error)}</Alert> : null}

      {account.data ? (
        <FoundAccount
          account={account.data}
          draft={draft}
          onDraftChange={setDraft}
          onRename={(value) =>
            rename.mutate({
              userId: account.data.userId,
              publicIdentifier: value,
            })
          }
          isRenaming={rename.isPending}
          renameError={rename.isError ? messageFor(rename.error) : null}
          renameSucceeded={rename.isSuccess}
        />
      ) : null}
    </section>
  );
}

/** Props of {@link FoundAccount}. */
interface FoundAccountProps {
  /** The account the lookup found. */
  account: AdminUserAccount;
  /** Handle the administrator is typing as the replacement. */
  draft: string;
  /** Receives the replacement as it is typed. */
  onDraftChange: (value: string) => void;
  /** Called with the handle to save. */
  onRename: (value: string) => void;
  /** Whether the rename request is in flight. */
  isRenaming: boolean;
  /** Message of a failed rename, or `null`. */
  renameError: string | null;
  /** Whether the last rename succeeded. */
  renameSucceeded: boolean;
}

/** The account the lookup found, with the facts an administrator needs and the rename form. */
function FoundAccount({
  account,
  draft,
  onDraftChange,
  onRename,
  isRenaming,
  renameError,
  renameSucceeded,
}: FoundAccountProps) {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-3 rounded-lg border border-border p-3">
      <dl className="grid gap-2 sm:grid-cols-2">
        <div className="flex flex-col gap-1">
          <dt className="text-xs text-muted-foreground">{t('auth.displayName')}</dt>
          <dd className="text-sm">{account.displayName}</dd>
        </div>
        <div className="flex flex-col gap-1">
          <dt className="text-xs text-muted-foreground">{t('auth.email')}</dt>
          <dd className="text-sm">
            <LtrText>{account.email ?? '—'}</LtrText>
          </dd>
        </div>
        <div className="flex flex-col gap-1">
          <dt className="text-xs text-muted-foreground">{t('auth.publicIdentifier')}</dt>
          <dd className="text-sm">
            {account.publicIdentifier ? (
              <LtrText>{account.publicIdentifier}</LtrText>
            ) : (
              <span className="text-muted-foreground">{t('library.none')}</span>
            )}
          </dd>
        </div>
        <div className="flex flex-col gap-1">
          <dt className="text-xs text-muted-foreground">{t('settings.memberSince')}</dt>
          <dd className="text-sm">
            <JalaliDate value={account.createdAtUtc} locale="fa" />
          </dd>
        </div>
      </dl>

      {account.isBanned ? (
        <Badge variant="outline">{t('admin.publicIdentifiers.banned')}</Badge>
      ) : null}

      {renameError ? <Alert variant="destructive">{renameError}</Alert> : null}
      {renameSucceeded ? (
        <Alert variant="success">{t('admin.publicIdentifiers.renamed')}</Alert>
      ) : null}

      <form
        className="flex flex-wrap items-end gap-2"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          onRename(draft.trim());
        }}
      >
        <div className="flex min-w-[16rem] flex-1 flex-col gap-1">
          <label className="text-xs text-muted-foreground" htmlFor="public-identifier-rename">
            {t('admin.publicIdentifiers.renameLabel')}
          </label>
          <LtrInput
            id="public-identifier-rename"
            value={draft}
            autoComplete="off"
            spellCheck={false}
            placeholder={t('auth.publicIdentifierPlaceholder')}
            onChange={(event) => onDraftChange(event.target.value)}
          />
          <p className="text-xs text-muted-foreground">{t('admin.publicIdentifiers.renameHint')}</p>
        </div>

        <Button type="submit" disabled={isRenaming || draft.trim().length === 0}>
          {isRenaming ? <Spinner /> : null}
          {t('admin.publicIdentifiers.renameAction')}
        </Button>
      </form>
    </div>
  );
}

/** The blacklist: its rows, the form that adds an entry and the release action on each row. */
function BlacklistSection() {
  const { t } = useTranslation();
  const messageFor = useProblemMessage();

  const [identifier, setIdentifier] = useState('');
  const [note, setNote] = useState('');

  const page = usePublicIdentifierBlacklist();
  const blacklist = useBlacklistPublicIdentifier();
  const release = useReleasePublicIdentifier();

  const rows = page.data?.items ?? [];
  const mutationError = blacklist.error ?? release.error;

  return (
    <section className="flex flex-col gap-3">
      <h3 className="text-sm font-medium">{t('admin.publicIdentifiers.blacklistTitle')}</h3>
      <p className="text-xs text-muted-foreground">{t('admin.publicIdentifiers.blacklistHint')}</p>

      {page.isPending ? (
        <div className="flex justify-center p-2">
          <Spinner />
        </div>
      ) : null}

      {page.isError ? <Alert variant="destructive">{messageFor(page.error)}</Alert> : null}
      {mutationError ? <Alert variant="destructive">{messageFor(mutationError)}</Alert> : null}

      {rows.length === 0 && !page.isPending ? (
        <p className="text-sm text-muted-foreground">
          {t('admin.publicIdentifiers.blacklistEmpty')}
        </p>
      ) : null}

      <ul className="flex flex-col gap-2">
        {rows.map((entry) => (
          <li
            key={entry.id}
            className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border p-3"
          >
            <div className="flex flex-col gap-1">
              <LtrText className="text-sm font-medium">{entry.identifier}</LtrText>
              {entry.note ? <p className="text-xs text-muted-foreground">{entry.note}</p> : null}
              <p className="text-xs text-muted-foreground">
                <JalaliDate value={entry.createdAtUtc} locale="fa" />
              </p>
            </div>

            <Button
              type="button"
              variant="outline"
              disabled={release.isPending}
              onClick={() => release.mutate(entry.id)}
            >
              {t('admin.publicIdentifiers.release')}
            </Button>
          </li>
        ))}
      </ul>

      <form
        className="flex flex-wrap items-end gap-2 border-t border-border pt-3"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          blacklist.mutate(
            {
              publicIdentifier: identifier.trim(),
              note: note.trim().length === 0 ? null : note.trim(),
            },
            {
              onSuccess: () => {
                setIdentifier('');
                setNote('');
              },
            },
          );
        }}
      >
        <div className="flex min-w-[12rem] flex-1 flex-col gap-1">
          <label className="text-xs text-muted-foreground" htmlFor="public-identifier-reserve">
            {t('admin.publicIdentifiers.reserveLabel')}
          </label>
          <LtrInput
            id="public-identifier-reserve"
            value={identifier}
            autoComplete="off"
            spellCheck={false}
            placeholder={t('auth.publicIdentifierPlaceholder')}
            onChange={(event) => setIdentifier(event.target.value)}
          />
        </div>

        <div className="flex min-w-[12rem] flex-1 flex-col gap-1">
          <label className="text-xs text-muted-foreground" htmlFor="public-identifier-note">
            {t('admin.publicIdentifiers.noteLabel')}
          </label>
          <Input
            id="public-identifier-note"
            value={note}
            onChange={(event) => setNote(event.target.value)}
          />
        </div>

        <Button type="submit" disabled={blacklist.isPending || identifier.trim().length === 0}>
          {blacklist.isPending ? <Spinner /> : null}
          {t('admin.publicIdentifiers.reserveAction')}
        </Button>
      </form>
    </section>
  );
}
